import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Row, Col, Alert, Spinner } from 'react-bootstrap';
import { Formik } from 'formik';
import * as Yup from 'yup';
import Select from 'react-select';
import { X, Briefcase, DollarSign, Users, ArrowRight } from 'lucide-react';
import axios from '../../../../services/AxiosInstance';
import { getApiUrl } from '../../../../utils/ApiUtils';

// Quick-create only. The full edit form lives on ProjectEditPage.
const BILLING_MODELS = [
  { value: 'time_materials', label: 'Tid & Material', desc: 'Fakturera arbetade timmar' },
  { value: 'fixed_price', label: 'Fastpris', desc: 'Fast kontraktssumma' },
  { value: 'retainer', label: 'Retainer', desc: 'Förbetald timbank' },
  { value: 'non_billable', label: 'Ej debiterbar', desc: 'Intern tid, ingen faktura' },
];

const COLORS = ['#3b82f6', '#10b981', '#f97316', '#ef4444', '#a855f7', '#ec4899', '#14b8a6', '#f59e0b', '#6366f1', '#78716c'];

const selectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: '38px',
    borderRadius: '6px',
    borderColor: state.isFocused ? 'var(--primary)' : '#d1d5db',
    boxShadow: state.isFocused ? '0 0 0 3px rgba(68,129,78,0.12)' : 'none',
    '&:hover': { borderColor: state.isFocused ? 'var(--primary)' : '#9ca3af' },
    fontSize: '0.875rem',
  }),
  menu: (base) => ({
    ...base,
    borderRadius: '8px',
    border: '1px solid #e5e7eb',
    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
    zIndex: 1050,
  }),
  option: (base, { isFocused, isSelected }) => ({
    ...base,
    backgroundColor: isSelected ? 'var(--primary)' : isFocused ? 'var(--rgba-primary-1)' : 'white',
    color: isSelected ? 'white' : '#1f2937',
    fontSize: '0.875rem',
    padding: '8px 12px',
  }),
  placeholder: (base) => ({ ...base, color: '#9ca3af', fontSize: '0.875rem' }),
  singleValue: (base) => ({ ...base, fontSize: '0.875rem' }),
};

const ProjectModal = ({ show, onHide, onSave, companyId, defaultName = '' }) => {
  const [customers, setCustomers] = useState([]);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  useEffect(() => {
    if (show && companyId) {
      axios.get(getApiUrl(`${companyId}/sales/customers`), { withCredentials: true })
        .then(res => {
          const data = Array.isArray(res.data) ? res.data : res.data.data || [];
          setCustomers(data);
        })
        .catch(() => {});
    }
  }, [show, companyId]);

  return (
    <Modal show={show} onHide={onHide} size="md" centered backdrop="static" dialogClassName="quick-project-modal">
      <Formik
        enableReinitialize
        initialValues={{
          name: defaultName || '',
          customerId: '',
          billingModel: 'time_materials',
          hourlyRate: '',
        }}
        validationSchema={Yup.object({
          name: Yup.string().max(120).required('Projektnamn krävs'),
          billingModel: Yup.string().oneOf(['time_materials', 'fixed_price', 'retainer', 'non_billable']).required(),
          hourlyRate: Yup.number().min(0).nullable(),
        })}
        onSubmit={async (values) => {
          setSaving(true); setSaveError(null);
          try {
            await onSave({
              name: values.name,
              // Defaults the user refines on the edit page. No client-side
              // code generation — backend accepts null.
              code: null,
              description: null,
              color: COLORS[0],
              projectType: 'client',
              billingModel: values.billingModel,
              customerId: values.customerId || null,
              clientReference: null,
              articleId: null,
              hourlyRate: values.hourlyRate ? parseFloat(values.hourlyRate) : null,
              budgetHours: null,
              fixedPriceAmount: null,
              currency: 'SEK',
              requireMembership: true,
            });
          } catch (err) {
            setSaveError(err.response?.data?.message || 'Kunde inte skapa projektet');
          } finally {
            setSaving(false);
          }
        }}
      >
        {({ values, errors, touched, handleChange, handleBlur, handleSubmit: formikSubmit, setFieldValue }) => (
          <Form onSubmit={formikSubmit}>
            <Modal.Header style={{ padding: '20px 24px', borderBottom: '1px solid #f1f5f9', alignItems: 'flex-start' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, flex: 1 }}>
                <div style={{
                  width: 40, height: 40, borderRadius: 8,
                  background: 'var(--rgba-primary-1)', color: 'var(--bs-primary)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                }}>
                  <Briefcase size={18} />
                </div>
                <div style={{ minWidth: 0 }}>
                  <Modal.Title style={{ fontSize: 17, fontWeight: 600, color: '#0f172a' }}>
                    Skapa nytt projekt
                  </Modal.Title>
                  <p style={{ fontSize: 12, color: '#64748b', margin: '2px 0 0' }}>
                    Fyll i det nödvändiga — du kompletterar resten direkt efteråt.
                  </p>
                </div>
              </div>
              <button type="button" onClick={onHide} aria-label="Stäng"
                style={{
                  background: 'white', border: '1px solid #e2e8f0', color: '#64748b',
                  width: 32, height: 32, borderRadius: 6, cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}
              >
                <X size={16} />
              </button>
            </Modal.Header>

            <Modal.Body style={{ padding: '20px 24px' }}>
              {saveError && <Alert variant="danger" dismissible onClose={() => setSaveError(null)}>{saveError}</Alert>}

              <Row className="g-3">
                <Col xs={12}>
                  <Form.Group>
                    <Form.Label className="fw-medium"><Briefcase size={14} className="me-2" />Projektnamn <span className="text-danger">*</span></Form.Label>
                    <Form.Control
                      name="name"
                      value={values.name}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      isInvalid={touched.name && errors.name}
                      placeholder="t.ex. Website Redesign"
                      autoFocus
                    />
                    <Form.Control.Feedback type="invalid">{errors.name}</Form.Control.Feedback>
                  </Form.Group>
                </Col>

                <Col xs={12}>
                  <Form.Group>
                    <Form.Label className="fw-medium"><Users size={14} className="me-2" />Kund (valfritt)</Form.Label>
                    <Select
                      options={customers.map(c => ({
                        value: c.id || c.customer_id,
                        label: `${c.name}${c.orgnumber ? ` (${c.orgnumber})` : ''}`,
                      }))}
                      value={values.customerId ? {
                        value: values.customerId,
                        label: (() => {
                          const c = customers.find(c => (c.id || c.customer_id) === values.customerId);
                          return c ? `${c.name}` : values.customerId;
                        })(),
                      } : null}
                      onChange={(option) => setFieldValue('customerId', option ? option.value : '')}
                      isClearable
                      isSearchable
                      placeholder="Sök eller välj kund…"
                      noOptionsMessage={() => 'Inga kunder hittades'}
                      styles={selectStyles}
                    />
                  </Form.Group>
                </Col>

                <Col xs={12}>
                  <Form.Label className="fw-medium"><DollarSign size={14} className="me-2" />Debitering</Form.Label>
                  <Row className="g-2">
                    {BILLING_MODELS.map(bm => (
                      <Col key={bm.value} xs={6}>
                        <div
                          onClick={() => setFieldValue('billingModel', bm.value)}
                          style={{
                            padding: '10px', borderRadius: 8, cursor: 'pointer',
                            border: values.billingModel === bm.value ? '2px solid var(--bs-primary)' : '1px solid #e5e7eb',
                            background: values.billingModel === bm.value ? 'var(--rgba-primary-1)' : '#fff',
                            transition: 'all 0.15s',
                          }}
                        >
                          <div className="fw-medium small">{bm.label}</div>
                          <div className="text-muted" style={{ fontSize: '0.65rem' }}>{bm.desc}</div>
                        </div>
                      </Col>
                    ))}
                  </Row>
                </Col>

                {values.billingModel !== 'non_billable' && (
                  <Col xs={12}>
                    <Form.Group>
                      <Form.Label className="fw-medium">Timpris (SEK/h)
                        {values.billingModel === 'time_materials' && <span className="text-danger"> *</span>}
                      </Form.Label>
                      <Form.Control
                        type="number"
                        name="hourlyRate"
                        value={values.hourlyRate}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        isInvalid={touched.hourlyRate && errors.hourlyRate}
                        placeholder="t.ex. 950"
                        min={0}
                        step="any"
                      />
                      <Form.Text className="text-muted" style={{ fontSize: '0.7rem' }}>
                        Kan justeras och kompletteras med specifika priser per person i projektinställningarna.
                      </Form.Text>
                    </Form.Group>
                  </Col>
                )}
              </Row>
            </Modal.Body>

            <Modal.Footer style={{ padding: '14px 24px', borderTop: '1px solid #f1f5f9', justifyContent: 'space-between' }}>
              <div style={{ fontSize: 11, color: '#94a3b8' }}>
                Kod, budget, team och beskrivning lägger du till på projektsidan.
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <Button variant="light" onClick={onHide} disabled={saving}>Avbryt</Button>
                <Button variant="primary" type="submit" disabled={saving}>
                  {saving
                    ? <><Spinner animation="border" size="sm" className="me-2" /> Skapar…</>
                    : <>Skapa & öppna <ArrowRight size={14} className="ms-1" /></>}
                </Button>
              </div>
            </Modal.Footer>
          </Form>
        )}
      </Formik>
    </Modal>
  );
};

export default ProjectModal;
